<template>
  <div class="p-2">
    <span class="header__label">Configuration History</span>
    <div v-if="configHistoryList.length">
      <div class="card__header mt-2">
        <div class="topic-timestamp"><b>Timestamp</b></div>
        <div class="topic-version"><b>Version</b></div>
      </div>
      <div v-for="(history, index) in configHistoryList" :key="index">
        <molecule-config-history-card
          :configHistory="history"
          @onClickCompareConfiguration="onClickCompareConfiguration"
          @onSelectedConfigData="onSelectedConfigData"
        ></molecule-config-history-card>
      </div>
      <div class="row mt-2 px-0 px-md-1 d-flex align-items-center">
        <div class="col-6">Showing {{ configHistoryList.length }} entries</div>
        <div class="col-6 d-flex justify-content-end">
          <b-pagination
            v-model="configHistoryCurrentPage"
            :total-rows="totalConfigHistory"
            :per-page="5"
            size="sm"
            class="my-0"
          ></b-pagination>
        </div>
      </div>
    </div>
    <div v-else class="d-flex flex-column align-items-center p-3">
      <feather-icon icon="CloudOffIcon" class="empty-data__icon"></feather-icon>
      <label class="empty-data__text--title">No Data</label>
    </div>
    <!-- ? Modal Section Start -->
    <molecule-modal-dialog
      modalId="modal-configuration-detail-history-card"
      :textTitle="`${targetNodeData.nodeName} (version ${targetNodeData.version})`"
      :textSubtitle="`Timestamp: ${formatDateTime(targetNodeData.timestamp)}`"
      :isButtonLeft="false"
      :isButtonRight="false"
      :isSlotBody="true"
      :isCloseOnBackDrop="false"
      @clickButtonRight="onClickCloseFromModal"
    >
      <template #body>
        <div class="mt-1 w-95">
          <div
            name="topic-and-button-wrapper"
            class="d-flex justify-content-between align-items-end mb-2"
          >
            <div name="LHS">
              <span class="modal-topic--text">Configuration Detail</span>
            </div>
            <div name="RHS">
              <atom-button
                buttonId="copy-generated-config"
                buttonStyle="border"
                class="mr-1"
                @click="onClickCopyConfigDetail"
              >
                <span v-if="screenWidth > 500" class="button-text">Copy</span>
                <feather-icon v-else icon="CopyIcon"></feather-icon>
              </atom-button>
              <atom-button
                buttonId="copy-generated-config"
                buttonStyle="border"
                @click="onClickDownloadConfigDetail"
              >
                <span v-if="screenWidth > 500" class="button-text"
                  >Download</span
                >
                <feather-icon v-else icon="DownloadIcon"></feather-icon>
              </atom-button>
            </div>
          </div>
          <div name="generated-config__wrapper">
            <prism-editor
              v-model="selectedConfigData"
              class="text-area-with-line-number custom-max-height"
              readonly
              :lineNumbers="screenWidth > 1080 ? true : false"
              :highlight="highlighter"
            ></prism-editor>
          </div>
        </div>
      </template>
    </molecule-modal-dialog>
    <!-- ? Modal Section End -->
    <textarea
      v-on:focus="$event.target.select()"
      readonly
      ref="copyNodeConfigHistoryTextBox"
      class="hidden-textbox"
      :value="selectedConfigData"
    />
  </div>
</template>

<script>
// import Prism Editor
import { PrismEditor } from "vue-prism-editor"
import "vue-prism-editor/dist/prismeditor.min.css"

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core"
import "prismjs/components/prism-clike"
import "prismjs/components/prism-javascript"
import "prismjs/themes/prism-tomorrow.css" // import syntax highlighting styles

import { BPagination } from "bootstrap-vue"
import { DateTime } from "luxon"
import AtomButton from "@/components/atoms/AtomButton.vue"
import MoleculeConfigHistoryCard from "@/components/molecules/networkConfigurationManagement/MoleculeConfigHistoryCard.vue"
import MoleculeModalDialog from "@/components/molecules/MoleculeModalDialog.vue"
import VueScreenSize from "vue-screen-size"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
export default {
  name: "MoleculeConfigHistory",
  components: {
    AtomButton,
    BPagination,
    MoleculeConfigHistoryCard,
    MoleculeModalDialog,
    PrismEditor,
  },
  props: {
    configHistoryList: {
      type: Array,
      required: false,
      default: () => [],
    },
    propConfigHistoryCurrentPage: {
      type: Number,
      required: false,
      default: 0,
    },
    totalConfigHistory: {
      type: Number,
      required: false,
      default: 0,
    },
    selectedConfigData: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return {
      configHistoryCurrentPage: 1,
      modalId: null,
      targetNodeData: {
        nodeName: "",
        version: 0,
        timestamp: "",
      },
    }
  },
  computed: {
    screenWidth() {
      return this.$vssWidth
    },
  },
  mixins: [mixins, mixinsValidator, VueScreenSize.VueScreenSizeMixin],
  watch: {
    propConfigHistoryCurrentPage: {
      immediate: true,
      handler() {
        if (this.propConfigHistoryCurrentPage) {
          this.configHistoryCurrentPage = this.propConfigHistoryCurrentPage
        }
      },
    },
    configHistoryCurrentPage(oldValue, newValue) {
      if (oldValue !== newValue) {
        this.$emit("onClickPagination", this.configHistoryCurrentPage)
      }
    },
  },
  methods: {
    formatDateTime(datetime) {
      if (this.hasString(datetime)) {
        return DateTime.fromISO(datetime).toFormat("dd/MM/yyyy HH:mm:ss")
      } else {
        return datetime
      }
    },

    highlighter(code) {
      return code // languages.<insert language> to return html with markup
    },

    onClickCompareConfiguration(selectedNodeId, selectedConfigVersion) {
      this.$emit(
        "onClickCompareConfiguration",
        selectedNodeId,
        selectedConfigVersion
      )
    },

    onSelectedConfigData(nodeName, nodeId, version, timestamp) {
      this.targetNodeData.nodeName = nodeName
      this.targetNodeData.version = version
      this.targetNodeData.timestamp = timestamp
      this.$emit("onSelectedConfigData", nodeId, version)
      this.openModal("modal-configuration-detail-history-card")
    },

    onClickCopyConfigDetail() {
      this.$refs.copyNodeConfigHistoryTextBox.focus()
      document.execCommand("copy")
      this.toastSuccess("Copy successfully")
    },

    onClickDownloadConfigDetail() {
      let blob = new Blob([this.selectedConfigData], { type: "text/plain" })
      let link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `${this.targetNodeData.nodeName}_version_${this.targetNodeData.version}.txt`
      link.click()
      this.toastSuccess("Download successfully")
    },

    onClickCloseFromModal() {
      this.closeModal()
    },

    // modal
    openModal(id) {
      this.modalId = id
      this.$bvModal.show(this.modalId)
    },

    closeModal() {
      this.$bvModal.hide(this.modalId)
      this.modalId = null
    },
  },
}
</script>

<style lang="scss" scoped>
.header__label {
  font-weight: 500;
  font-size: 18px;
  color: #5e5873;
}

.card__header {
  display: none;
}

.topic-timestamp {
  grid-area: topic-timestamp;
  margin-left: 20px;
}

.topic-version {
  grid-area: topic-version;
}

.empty-data__icon {
  width: 100px;
  height: 100px;
}

.empty-data__text--title {
  color: #5e5873;
  font-weight: 500;
  font-size: 18px;
  margin-top: 20px;
}

.custom-max-height {
  max-height: 500px;
  overflow-y: auto;
}

.hidden-textbox {
  position: absolute;
  top: -99999px;
}

.modal-topic--text {
  font-size: 12px;
}

// Small devices (landscape phones, 576px and up)
@media screen and (min-width: 576px) {
  .modal-topic--text {
    font-size: 14px;
  }
}

// Medium devices (tablets, 768px and up)
@media screen and (min-width: 768px) {
  .card__header {
    display: grid;
    grid-template-columns: 2.25fr 2fr 1fr;
    grid-template-areas: "topic-timestamp topic-version .";
  }
}

// Large devices (desktops, 992px and up)
@media screen and (min-width: 992px) {
}

// X-Large devices (large desktops, 1200px and up)
@media screen and (min-width: 1200px) {
}
</style>
